import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  transactionCategoriesDB,
  LearnedPattern,
  LearnedPatternInput,
} from "../../services/database/transactionCategoriesDB";
import {
  US_FEDERAL_TAX_CATEGORIES,
  OREGON_STATE_TAX_CATEGORIES,
} from "../../lib/constants";

const Rules: React.FC = () => {
  const [rules, setRules] = useState<LearnedPattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<LearnedPattern | null>(null);
  const [form, setForm] = useState<LearnedPatternInput>({
    merchant_pattern: "",
    transaction_pattern: "",
    general_category: "",
    federal_category: "",
    oregon_category: "",
    confidence_score: 1,
  });

  const loadRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await transactionCategoriesDB.getLearnedPatterns();
      setRules(next);
    } catch (e) {
      console.error("[Rules] Failed to load rules:", e);
      setError("Failed to load rules.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadRules();
  }, [loadRules]);

  const filteredRules = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rules;
    return rules.filter((rule) => {
      const haystack = [
        rule.merchant_pattern,
        rule.transaction_pattern,
        rule.general_category,
        rule.federal_category,
        rule.oregon_category,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rules, search]);

  const openCreate = () => {
    setEditingRule(null);
    setForm({
      merchant_pattern: "",
      transaction_pattern: "",
      general_category: "",
      federal_category: "",
      oregon_category: "",
      confidence_score: 1,
    });
    setOpen(true);
  };

  const openEdit = (rule: LearnedPattern) => {
    setEditingRule(rule);
    setForm({
      merchant_pattern: rule.merchant_pattern || "",
      transaction_pattern: rule.transaction_pattern || "",
      general_category: rule.general_category || "",
      federal_category: rule.federal_category || "",
      oregon_category: rule.oregon_category || "",
      confidence_score: rule.confidence_score ?? 1,
      times_used: rule.times_used,
      times_confirmed: rule.times_confirmed,
      times_rejected: rule.times_rejected,
    });
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingRule(null);
  };

  const handleSave = async () => {
    if (!form.general_category?.trim()) {
      setError("General category is required.");
      return;
    }
    if (!form.merchant_pattern?.trim() && !form.transaction_pattern?.trim()) {
      setError("Add a merchant pattern or transaction pattern.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editingRule?.id) {
        const updated = await transactionCategoriesDB.updateLearnedPattern(
          editingRule.id,
          form
        );
        if (!updated) {
          throw new Error("Failed to update rule");
        }
      } else {
        const created =
          await transactionCategoriesDB.createLearnedPattern(form);
        if (!created) {
          throw new Error("Failed to create rule");
        }
      }
      closeDialog();
      await loadRules();
    } catch (e) {
      console.error("[Rules] Save error:", e);
      setError("Failed to save rule.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule: LearnedPattern) => {
    if (!rule.id) return;
    setSaving(true);
    setError(null);
    try {
      const ok = await transactionCategoriesDB.deleteLearnedPattern(rule.id);
      if (!ok) {
        throw new Error("Delete failed");
      }
      await loadRules();
    } catch (e) {
      console.error("[Rules] Delete error:", e);
      setError("Failed to delete rule.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={1.25}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Rules
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Edit the same Smart AI Categorization rules used on Transactions.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          placeholder="Search rules..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          disabled={saving}
          size="small"
        >
          Add Rule
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.5, px: 1 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Merchant Pattern</TableCell>
              <TableCell>Transaction Pattern</TableCell>
              <TableCell>General</TableCell>
              <TableCell>Federal</TableCell>
              <TableCell>Oregon</TableCell>
              <TableCell align="right">Confidence</TableCell>
              <TableCell align="right">Times Used</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8}>Loading rules...</TableCell>
              </TableRow>
            ) : filteredRules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>No rules found.</TableCell>
              </TableRow>
            ) : (
              filteredRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>{rule.merchant_pattern || "—"}</TableCell>
                  <TableCell>{rule.transaction_pattern || "—"}</TableCell>
                  <TableCell>{rule.general_category || "—"}</TableCell>
                  <TableCell>{rule.federal_category || "—"}</TableCell>
                  <TableCell>{rule.oregon_category || "—"}</TableCell>
                  <TableCell align="right">
                    {(rule.confidence_score ?? 0).toFixed(2)}
                  </TableCell>
                  <TableCell align="right">{rule.times_used ?? 0}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => openEdit(rule)}
                          disabled={saving}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => void handleDelete(rule)}
                          disabled={saving}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="xs">
        <DialogTitle>{editingRule ? "Edit Rule" : "Add Rule"}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label="Merchant Pattern"
              value={form.merchant_pattern || ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  merchant_pattern: e.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              size="small"
              label="Transaction Pattern"
              value={form.transaction_pattern || ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  transaction_pattern: e.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              size="small"
              label="General Category"
              value={form.general_category || ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  general_category: e.target.value,
                }))
              }
              fullWidth
              required
            />
            <TextField
              size="small"
              select
              label="Federal Category"
              value={form.federal_category || ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  federal_category: e.target.value,
                }))
              }
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              {US_FEDERAL_TAX_CATEGORIES.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label="Oregon Category"
              value={form.oregon_category || ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  oregon_category: e.target.value,
                }))
              }
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              {OREGON_STATE_TAX_CATEGORIES.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Confidence (0-1)"
              type="number"
              inputProps={{ min: 0, max: 1, step: 0.01 }}
              value={form.confidence_score ?? 1}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  confidence_score: Number(e.target.value || 1),
                }))
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving} size="small">
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => void handleSave()}
            disabled={saving}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default Rules;
